import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Pagination } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxGreenTextButton, sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import DefaultButton from '../../../components/Accessibility/DefaultButton';
import VoltarButton from '../../../components/Accessibility/VoltarButton';
import LoadingApp from '../../../components/LoadingApp';
import ModalAction from '../../../components/ModalAction';
import ModalFeedBack from '../../../components/ModalFeedBack';
import ModalNewPhone from '../../../components/ModalNewPhone';
import { Agendamento } from '../../../interfaces/entities/Agendamento';
import { Mensagem } from '../../../interfaces/entities/Mensagem';
import { ItemCronogramaGC } from '../../../interfaces/entities/SaudeBemEstar/GestaoDeCronicos/ItemCronogramaGC';
import { cancelarAtendimento } from '../../../services/AgendamentoService';
import { getCronograma, getHomeGestao, getInstrucoesAtendimento, getMeusAtendimentos } from '../../../services/SaudeBemEstar/GestaoDeCronicosService';
import { scrollTo } from '../../../utils/ScrollUtil';
import ModalCronogramaGC from '../ModalCronogramaGC';
import ScheduleCardGC from './ScheduleCardGC';
import SchedulesStatusFilter from './SchedulesStatusFilter';
import './styles.css';
import { sxPagination } from './sxProps';
import PageTitle from '../../../components/Accessibility/PageTitle';

const AgendamentosGC: React.FC = () => {

  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [schedules, setSchedules] = useState<Agendamento[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Agendamento | null>(null);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [availableSchedule, setAvailableSchedule] = useState<boolean>(false);
  const [cronogramaCompletoGC, setCronogramaCompletoGC] = useState<ItemCronogramaGC[]>();
  const [showModalNovoNumero, setShowModalNovoNumero] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning' | 'none', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);
  const [modalAction, setModalAction] = useState<null | {
    handleShowModal?: () => void; textOutlinedButton: string; textContainedButton: string; title: string; subTitle?: string;
    handleOutlinedButton?: () => void; handleContainedButton?: () => void; icon?: "error" | "success" | "warning"
  }>(null);
  const [modalCronograma, setModalCronograma] = useState<null | { closeAction: () => void }>(null);

  const accessibility = useAccessibility();

  const fetchSchedules = async () => {
    setLoading(true);
    const result = await getMeusAtendimentos();
    if (result !== null) {
      setSchedules(result.data.listaMeusAtendimentos);
      await fetchAvailableNewSchedule();
    } else {
      setSchedules([]);
      console.error('Falha ao buscar agendamentos.');
    }
    setLoading(false);
  };

  const fetchAvailableNewSchedule = async () => {
    const response = await getHomeGestao();
    if (response?.status === 200) {
      setAvailableSchedule(response.data.isPermiteAgedamento);
    } else {
      console.error(response);
      openModalFeedback('Erro', 'Ocorreu um erro ao consultar a disponibilidade de novos agendamentos.', 'Fechar',
        closeModalFeedback, closeModalFeedback, 'error', true
      );
    }
  }

  const fetchCronograma = async () => {
    setLoading(true);
    const response = await getCronograma();
    if (response?.status === 200) {
      setCronogramaCompletoGC(response.data);
    } else {
      console.error(response);
      openModalFeedback('Erro', 'Ocorreu um erro ao consultar o cronograma.', 'Fechar',
        closeModalFeedback, closeModalFeedback, 'error', true
      );
    }
    setLoading(false);
  }

  const handleConfirmCancelarAtendimento = async () => {
    closeModalAction();
    if (selectedSchedule === null) return;
    setLoading(true);
    let response = await cancelarAtendimento(selectedSchedule.id, selectedSchedule.idInstal);
    if (response?.status !== 200) {
      openModalFeedback('Erro', 'Ocorreu um erro ao cancelar o atendimento', 'Fechar', closeModalFeedback, closeModalFeedback, 'error');
    } else {
      openModalFeedback('Atenção!', 'Seu atendimento foi cancelado!', '',
        () => { closeModalFeedback(); fetchSchedules(); }, () => { closeModalFeedback(); fetchSchedules(); }, 'none'
      );
    }
    setLoading(false);
  }

  const openModalFeedback = (
    title: string, subTitle: string, textButton: string, closeAction: () => void,
    buttonAction: () => void, icon?: 'success' | 'warning' | 'error' | 'none', showButtonModal?: boolean
  ) => {
    setModalFeedback({
      icon,
      title,
      subTitle,
      textButton,
      showButtonModal: showButtonModal || false,
      closeAction: closeAction,
      buttonAction: buttonAction
    });
  }

  const closeModalFeedback = () => {
    setModalFeedback(null);
  }

  const openModalNovoNumero = (schedule: Agendamento) => {
    setShowModalNovoNumero(true);
  }

  const openModalAction = (
    title: string, subTitle: string, textOutlinedButton: string, textContainedButton: string,
    handleOutlinedButton?: () => void, handleContainedButton?: () => void, icon?: "error" | "success" | "warning"
  ) => {
    setModalAction({
      title: title,
      subTitle: subTitle,
      textOutlinedButton: textOutlinedButton,
      textContainedButton: textContainedButton,
      handleContainedButton: handleContainedButton,
      handleOutlinedButton: handleOutlinedButton,
      handleShowModal: closeModalAction,
      icon: icon || 'warning'
    })
  }

  const closeModalAction = () => {
    setModalAction(null);
  }

  const handleClickCancelarAtendimento = (schedule: Agendamento | null) => {
    setSelectedSchedule(schedule);
    openModalAction('Cancelar Atendimento', 'Tem certeza que deseja cancelar seu atendimento?', 'Voltar', 'Cancelar', closeModalAction, handleConfirmCancelarAtendimento);
  }

  const openModalCronograma = () => {
    setModalCronograma({
      closeAction: () => setModalCronograma(null),
    });
  }

  const buildHtmlStringInstrucoesAtendimento = (instrucoes: Mensagem[]): string => {
    return instrucoes.map((instrucao) =>
      `<b>${instrucao.titulo}</b> <br/><br/> ${instrucao.textos.map((texto) => `${texto}`).join(' ')}`
    ).join('<br/><br/>');
  }

  const openModalInstrucoes = async (tipoAtend: string) => {
    setLoading(true);
    const response = await getInstrucoesAtendimento(tipoAtend);
    if (response?.status === 200) {
      openModalFeedback('Instruções de atendimento', buildHtmlStringInstrucoesAtendimento(response.data), '', closeModalFeedback, closeModalFeedback, 'none');
    } else {
      console.error(response);
      openModalFeedback('Erro', 'Ocorreu um erro ao consultar seu progresso do programa ', 'Fechar',
        closeModalFeedback, closeModalFeedback, 'error', true
      );
    }
    setLoading(false);
  }

  const viewCompleteSchedule = async () => {
    if (!cronogramaCompletoGC) {
      fetchCronograma();
    }
    openModalCronograma();
  }

  useEffect(() => {
    fetchSchedules();

    const threeMinutesRefresh = 3 * 60 * 1000;
    const intervalId = setInterval(() => {
      fetchSchedules();
    }, threeMinutesRefresh);

    return () => clearInterval(intervalId);
  }, [])

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTo('schedules-container');
  };

  const statusFilteredSchedules = schedules.filter((schedule) => {
    return statusFilter === 'todos' || schedule.status === statusFilter;
  });

  const paginatedSchedules = statusFilteredSchedules.slice(
    (page - 1) * 5,
    page * 5
  );

  return (
    <>
      <LoadingApp showLoading={loading} text={'Carregando...'} />
      <div className='schedules-container' id='schedules-container'>
        <PageTitle text="Meus atendimentos" marginBottom="24px" />

        {availableSchedule &&
          <div style={{marginBottom: '24px'}}>
            <DefaultButton
              onClick={() => navigate('/saudeBemEstar/gestaoDeCronicos/agendar')}
              text='NOVO ATENDIMENTO'
            />
          </div>
        }

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {(schedules.length > 0) && <SchedulesStatusFilter statusFilter={statusFilter} onStatusChange={handleStatusFilterChange} />}
          <DefaultButton
            sx={sxGreenTextButton}
            onClick={() => viewCompleteSchedule()}
            variant='text'
          >
            <u style={accessibility.textStyleAccessibility}>ver cronograma completo</u>&nbsp;&nbsp;
            <FontAwesomeIcon style={accessibility.textStyleAccessibility} icon={faChevronRight} />
          </DefaultButton>
        </div>

        <div className='schedule-cards-holder'>
          {statusFilteredSchedules.length === 0 &&
            <h1 className='empty-schedules' style={accessibility.textStyleAccessibility}>Nenhum agendamento encontrado.</h1>
          }
          {statusFilteredSchedules.length > 0 && (paginatedSchedules.map((schedule) => (
            <ScheduleCardGC
              agendamento={schedule}
              key={schedule.id}
              handleClickCancelarAtendimento={() => { handleClickCancelarAtendimento(schedule) }}
              handleClickEscolherOutroNumero={() => { openModalNovoNumero(schedule) }}
              handleClickOpenModalInstrucoes={() => openModalInstrucoes(schedule.descrTipoAtend)}
              handleClickConfirmarAgendamento={() => { }}
            />
          )))}
        </div>

        {statusFilteredSchedules.length > 5 && !loading &&
          <div className='pagination-holder'>
            <Pagination
              count={Math.ceil(statusFilteredSchedules.length / 5)}
              sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
              color='primary'
              page={page}
              onChange={handlePageChange}
            />
          </div>
        }
        <VoltarButton onClick={() => window.history.back()} />
      </div>
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
        classNameModal='modal-instrucao'
      />
      <ModalAction
        showModal={!!modalAction}
        handleShowModal={modalAction?.handleShowModal}
        icon={modalAction?.icon}
        textOutlinedButton={modalAction?.textOutlinedButton || ''}
        textContainedButton={modalAction?.textContainedButton || ''}
        title={modalAction?.title || ''}
        subTitle={modalAction?.subTitle || ''}
        handleOutlinedButton={modalAction?.handleOutlinedButton}
        handleContainedButton={modalAction?.handleContainedButton}
      />
      <ModalNewPhone handleChangeNumber={() => { }} handleShowModal={() => setShowModalNovoNumero(show => !show)} showModal={showModalNovoNumero} />
      {cronogramaCompletoGC &&
        <ModalCronogramaGC
          showModal={!!modalCronograma}
          handleShowModal={modalCronograma?.closeAction}
          itensCronograma={cronogramaCompletoGC}
        />
      }
    </>
  )
}

export default AgendamentosGC;